import { useMemo, useState } from 'react';

import {
  downloadAssociateCombinedReport,
  downloadAssociateWorkloadReport,
  downloadExecutiveReport,
  downloadFollowUpReport,
  downloadHistoricalAssignmentsReport,
  downloadPageSourceReport,
  downloadSurveyScoresPdf,
  exportCSV,
} from '../api/client.js';
import Layout from '../components/Layout.jsx';
import ReportCard from '../components/ReportCard.jsx';
import { useAuth } from '../context/AuthContext.jsx';

// Presets emit either (start, end) date strings or null → null meaning
// "no bound", which the backend interprets as an all-time report.
function presetRange(preset) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const end = iso(today);
  if (preset === 'today') return { start: end, end };
  if (preset === 'week') {
    const d = new Date(today);
    d.setDate(d.getDate() - 6);
    return { start: iso(d), end };
  }
  if (preset === 'month') {
    const d = new Date(today);
    d.setDate(d.getDate() - 29);
    return { start: iso(d), end };
  }
  return { start: '', end: '' }; // all time
}

/** Detect which preset (if any) is currently applied — used to highlight
 * the matching button. Matches the same anchor-to-today windows the
 * backend uses for its date_range_label. */
function detectActivePreset(range) {
  const today = new Date().toISOString().slice(0, 10);
  if (!range.start && !range.end) return 'all';
  if (range.start === today && range.end === today) return 'today';
  if (range.end !== today) return null;
  const days = Math.round(
    (Date.parse(range.end) - Date.parse(range.start)) / (1000 * 60 * 60 * 24),
  );
  if (days === 6) return 'week';
  if (days === 29) return 'month';
  return null;
}

/** Small controlled block: start/end date inputs + preset buttons.
 * Setting `showPresetButtons` renders all four preset shortcuts and
 * highlights the active one; without it, presets are hidden entirely. */
function DateRangeControls({ start, end, onChange, showPresetButtons = true }) {
  const active = detectActivePreset({ start, end });

  function applyPreset(p) {
    const r = presetRange(p);
    onChange({ start: r.start, end: r.end });
  }
  const presetBtn = (key, label) => (
    <button
      type="button"
      onClick={() => applyPreset(key)}
      className={
        'report-card__preset' +
        (active === key ? ' report-card__preset--active' : '')
      }
    >
      {label}
    </button>
  );
  return (
    <div className="report-card__range">
      <label className="report-card__field">
        <span>Start</span>
        <input
          type="date"
          value={start}
          onChange={(e) => onChange({ start: e.target.value, end })}
        />
      </label>
      <label className="report-card__field">
        <span>End</span>
        <input
          type="date"
          value={end}
          onChange={(e) => onChange({ start, end: e.target.value })}
        />
      </label>
      {showPresetButtons && (
        <div className="report-card__presets">
          {presetBtn('today', 'Today')}
          {presetBtn('week', 'Last 7 Days')}
          {presetBtn('month', 'Last 30 Days')}
          {presetBtn('all', 'All Time')}
        </div>
      )}
    </div>
  );
}

export default function ReportsPage() {
  const { isAdmin } = useAuth();
  const admin = isAdmin();

  const [execRange, setExecRange] = useState({ start: '', end: '' });
  const [followUpRange, setFollowUpRange] = useState({ start: '', end: '' });
  const [followUpStatus, setFollowUpStatus] = useState('all');
  const [workloadRange, setWorkloadRange] = useState({ start: '', end: '' });
  const [combinedRange, setCombinedRange] = useState({ start: '', end: '' });
  const [fullRange, setFullRange] = useState({ start: '', end: '' });
  const [fullStatus, setFullStatus] = useState('');
  const [fullSeverity, setFullSeverity] = useState('');
  const [pageSourceRange, setPageSourceRange] = useState({ start: '', end: '' });
  const [surveyScoresRange, setSurveyScoresRange] = useState({ start: '', end: '' });

  const rangeParams = useMemo(
    () => (range) => {
      const p = {};
      if (range.start) p.start_date = range.start;
      if (range.end) p.end_date = range.end;
      return p;
    },
    [],
  );

  if (!admin) {
    return (
      <Layout title="Reports">
        <div className="reports-empty">
          Reports are available to admin users only.
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Reports">
      <div className="reports-grid">
        <ReportCard
          title="Executive Summary"
          description="Snapshot of overall feedback trends, severity breakdown, and insights. Suitable for leadership."
          buttonLabel="Download Report"
          onGenerate={() => downloadExecutiveReport(rangeParams(execRange))}
        >
          <DateRangeControls
            start={execRange.start}
            end={execRange.end}
            onChange={setExecRange}
          />
        </ReportCard>

        <ReportCard
          title="Customer Experience Scores"
          description="Customer Effort Score (CES) trend with the Evolution go-live marker and pre/post comparison, plus pie charts for the two survey questions."
          buttonLabel="Download PDF"
          onGenerate={() => downloadSurveyScoresPdf(rangeParams(surveyScoresRange))}
        >
          <DateRangeControls
            start={surveyScoresRange.start}
            end={surveyScoresRange.end}
            onChange={setSurveyScoresRange}
          />
        </ReportCard>

        <ReportCard
          title="Client Follow-Up"
          description="All submissions where follow-up was requested, with handler notes and resolution status."
          buttonLabel="Download CSV"
          onGenerate={() =>
            downloadFollowUpReport({
              ...rangeParams(followUpRange),
              ...(followUpStatus !== 'all' ? { status: followUpStatus } : {}),
            })
          }
        >
          <DateRangeControls
            start={followUpRange.start}
            end={followUpRange.end}
            onChange={setFollowUpRange}
          />
          <label className="report-card__field">
            <span>Status</span>
            <select
              value={followUpStatus}
              onChange={(e) => setFollowUpStatus(e.target.value)}
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
            </select>
          </label>
        </ReportCard>

        <ReportCard
          title="Associate Workload"
          description="Productivity summary by team member — tickets assigned, completed, and average turnaround."
          buttonLabel="Download CSV"
          onGenerate={() =>
            downloadAssociateWorkloadReport(rangeParams(workloadRange))
          }
        >
          <DateRangeControls
            start={workloadRange.start}
            end={workloadRange.end}
            onChange={setWorkloadRange}
          />
        </ReportCard>

        <ReportCard
          title="All-Time Assignment History"
          description="Total tickets completed per team member since the dashboard launched."
          buttonLabel="Download CSV"
          onGenerate={() => downloadHistoricalAssignmentsReport()}
        />

        <ReportCard
          title="Associate Combined Report"
          description="Current workload and all-time assignment history merged into one file with two labeled sections."
          buttonLabel="Download CSV"
          onGenerate={() =>
            downloadAssociateCombinedReport(rangeParams(combinedRange))
          }
        >
          <DateRangeControls
            start={combinedRange.start}
            end={combinedRange.end}
            onChange={setCombinedRange}
          />
        </ReportCard>

        <ReportCard
          title="Full Data Export"
          description="All submissions with every field. Use for offline analysis."
          buttonLabel="Export CSV"
          onGenerate={() =>
            exportCSV({
              ...(fullRange.start ? { start_date: fullRange.start } : {}),
              ...(fullRange.end ? { end_date: fullRange.end } : {}),
              ...(fullStatus ? { status: fullStatus } : {}),
              ...(fullSeverity ? { severity: fullSeverity } : {}),
            })
          }
        >
          <DateRangeControls
            start={fullRange.start}
            end={fullRange.end}
            onChange={setFullRange}
          />
          <label className="report-card__field">
            <span>Status</span>
            <select
              value={fullStatus}
              onChange={(e) => setFullStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="unreviewed">Unreviewed</option>
              <option value="in_progress">In Progress</option>
              <option value="escalated">Escalated</option>
              <option value="pending_complete">Pending Complete</option>
              <option value="resolved">Resolved</option>
              <option value="no_action_needed">No Action Needed</option>
            </select>
          </label>
          <label className="report-card__field">
            <span>Severity</span>
            <select
              value={fullSeverity}
              onChange={(e) => setFullSeverity(e.target.value)}
            >
              <option value="">All severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </label>
        </ReportCard>

        <ReportCard
          title="Feedback by Page"
          description="Which centier.com pages are generating the most feedback and where friction is highest."
          buttonLabel="Download CSV"
          onGenerate={() =>
            downloadPageSourceReport(rangeParams(pageSourceRange))
          }
        >
          <DateRangeControls
            start={pageSourceRange.start}
            end={pageSourceRange.end}
            onChange={setPageSourceRange}
          />
        </ReportCard>
      </div>
    </Layout>
  );
}
